<script>
import { mapActions, mapState } from 'vuex';
import DeleteWarning from './delete-warning.vue';
import Card from "../../../components/Card.vue";
import i18n from "@/plugins/i18n";

export default {
    components: {
        DeleteWarning,
        Card,
    },

    props: {
        selectedCompanyId: {
            type: Number,
            required: true
        },

        userId: {
            type: Number,
            required: true
        },

        years: {
            type: Object,
            required: true
        },
    },

    data() {
        return {
            loading: false,

            items: null,
            fallbackValue: null,

            itemToAdd: {
                product_group_id: null,
                product_group_name: '',
                product_group_name_tr: '',
                production_year: null,
                production_percent: null,
                production_count: null,
                production_tonnes: null,
                production_revenue_share: null,
            },
            addingItem: false,

            deletingItem: null,

            selectedProductYear: null,
            maxProductYear: new Date().getFullYear() -1,
        }
    },

    computed: {
        flex_options() {
            return {
                small: 2,
                mid: 4,
            };
        },

        headers() {
            const lang = this.selectedReportLanguages;
            const defaults = [
                { key: 'share', title: i18n.t('companyProfile.modal7.share'), flex: this.flex_options.mid },
                { key: 'number', title: i18n.t('companyProfile.modal7.number'), flex: this.flex_options.mid },
                { key: 'tonnes', title: i18n.t('companyProfile.modal7.tonnes'), flex: this.flex_options.mid },
                { key: 'rev_share', title: i18n.t('companyProfile.modal7.rev_share'), flex: this.flex_options.mid },
            ]

            if (lang.some(x => x === 'en') && lang.some(x => x === 'tr')) {
                return [
                    { key: 'delete', title: '', flex: this.flex_options.small },
                    { key: 'name', title: i18n.t('companyProfile.modal7.product'), flex: this.flex_options.mid },
                    { key: 'name_tr', title: i18n.t('companyProfile.modal7.product_tr'), flex: this.flex_options.mid },
                    ...defaults
                ];
            } else if (lang.some(x => x === 'en') && !lang.some(x => x === 'tr')) {
                return [
                    { key: 'delete', title: '', flex: this.flex_options.small },
                    { key: 'name', title: i18n.t('companyProfile.modal7.product'), flex: this.flex_options.mid },
                    ...defaults
                ];
            } else if (!lang.some(x => x === 'en') && lang.some(x => x === 'tr')) {
                return [
                    { key: 'delete', title: '', flex: this.flex_options.small },
                    { key: 'name', title: i18n.t('companyProfile.modal7.product_tr'), flex: this.flex_options.mid },
                    ...defaults
                ];
            } else {
                return [
                    { key: 'delete', title: '', flex: this.flex_options.small },
                    { key: 'name', title: i18n.t('companyProfile.modal7.product'), flex: this.flex_options.mid, no_lang_selected: true },
                    ...defaults
                ];
            }
        },

        ...mapState('app', [
            'selectedReportLanguages'
        ]),

        productYears() {
            return [this.maxProductYear, this.maxProductYear-1, this.maxProductYear-2, this.maxProductYear-3, this.maxProductYear-4 ];
        },

        filteredItems() {
            if (this.items === null || !this.selectedProductYear) return [];
            return this.items.filter(x => x.production_year === this.selectedProductYear);
        },

    },

    mounted() {
        this.getItems();
    },

    methods: {
        ...mapActions('company', [
            'getProducts',
            'createProducts',
            'updateProduct',
            'deleteProduct',
        ]),

        getItems() {
            this.setLoading(true);
            const params = {
                company_id: this.selectedCompanyId,
                is_deleted: false
            };

            this.getProducts(params)
                .then(res => {
                    this.items = res.data;
                })
                .finally(() => {
                    let max_year = -1;
                    for (let i = 0; i < this.items.length; i++) {
                        const item = this.items[i];
                        if (item.production_year > max_year) {
                            max_year = item.production_year;
                        }
                    }
                    
                    if (max_year != -1) {
                        this.setSelectedProductYear(max_year);
                        this.maxProductYear = max_year;
                    } else {
                        this.setSelectedProductYear(new Date().getFullYear() - 1);
                        this.maxProductYear = new Date().getFullYear() - 1;
                    }

                    this.setLoading(false);
                })
        },

        createItem() {
            if (this.itemToAdd.production_year === null || this.itemToAdd.production_year < 1900) return;
            if (!this.validateItem(this.itemToAdd)) return;

            const params = {
                company_id: this.selectedCompanyId,
                created_by: this.userId,
                production_percent: this.itemToAdd.production_percent,
                product_group_name: this.itemToAdd.product_group_name,
                product_group_name_tr: this.itemToAdd.product_group_name_tr,
                production_year: this.itemToAdd.production_year,
                production_count: this.itemToAdd.production_count,
                production_tonnes: this.itemToAdd.production_tonnes,
                production_revenue_share: this.itemToAdd.production_revenue_share,
            };

            this.createProducts(params)
                .then(res => {
                    this.itemToAdd.product_group_id = res.data.product_group_id;
                    this.items.push(this.itemToAdd);

                    if (this.items.length === 1) {
                        this.setSelectedProductYear(params.production_year);
                    }

                    this.handleResetAddingItem();

                    this.setAddingItem(false);

                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })

        },

        updateItem(e, key, id) {
            let value = null;
            if (!e.target.value || e.target.value.length === 0) {
                value = null;
            } else {
                value = e.target.value;
            }
            
            let sum = 0;
            if (key === 'production_percent') {
                for (let i = 0; i < this.filteredItems.length; i++) {
                    sum += Number(this.filteredItems[i].production_percent);
                    if (sum > 100) break;
                }
            }

            let sum2 = 0;
            if (key === 'production_revenue_share') {
                for (let i = 0; i < this.filteredItems.length; i++) {
                    sum2 += Number(this.filteredItems[i].production_revenue_share);
                    if (sum2 > 100) break;
                }
            }

            if (sum > 100 || sum2 > 100) {
                this.$notify('error', i18n.t('companyProfile.modal7.percentageWarning'), true);
                return;
            }

            const params = {
                product_group_id: id,
                body: {
                    [`${key}`]: value
                }
            }

            this.updateProduct(params)
                .then(res => {
                    this.setFallbackValue(null);
                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.items.find(x => x.product_group_id == id)[`${key}`] == this.fallbackValue;
                    this.setFallbackValue(null);
                    this.$notify('error', response.data.messageName);
                })
        },

        /**
         * Validates the item before proceeding to create the item.
         * @returns {boolean} False if validation failed, True if validation passed.
        */
        validateItem(item) {
            const lang = this.selectedReportLanguages;
            if (lang.some(x => x === 'en') && lang.some(x => x === 'tr')) {
                if (item.product_group_name === '' || item.product_group_name === null) {
                    return false;
                }
            } else if (lang.some(x => x === 'en') && !lang.some(x => x === 'tr')) {
                if (item.product_group_name === '' || item.product_group_name === null) {
                    return false;
                }
            } else if (!lang.some(x => x === 'en') && lang.some(x => x === 'tr')) {
                if (item.product_group_name_tr === '' || item.product_group_name_tr === null) {
                    return false;
                }
            } else {
                if (item.product_group_name === '' || item.product_group_name === null) {
                    return false;
                }
            }

            let sum = 0;
            for (let i = 0; i < this.filteredItems.length; i++) {
                sum += Number(this.filteredItems[i].production_percent);
                if (sum > 100) break;
            }

            sum += Number(item.production_percent);

            let sum2 = 0;
            for (let i = 0; i < this.filteredItems.length; i++) {
                sum2 += Number(this.filteredItems[i].production_revenue_share);
                if (sum2 > 100) break;
            }

            sum2 += Number(item.production_revenue_share);

            if (sum > 100 || sum2 > 100) {
                this.$notify('error', i18n.t('companyProfile.modal7.percentageWarning'), true);
                return false;
            }

            return true;
        },

        deleteItem() {
            this.deleteProduct({ product_group_id: this.deletingItem.product_group_id })
                .then(() => {
                    this.items = this.items.filter(x => x.product_group_id != this.deletingItem.product_group_id);
                    this.setDeletingItem(null);
                    this.$notify('success', '', true);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        /* handlers */

        handleResetAddingItem() {
            this.itemToAdd = {
                product_group_id: null,
                product_group_name: '',
                product_group_name_tr: '',
                production_year: null,
                production_percent: null,
                production_count: null,
                production_tonnes: null,
                production_revenue_share: null,
            };

            this.setAddingItem(false);
        },

        /* setters */

        setLoading(bool) {
            this.loading = bool;
        },

        setSelectedProductYear(year) {
            this.handleResetAddingItem();
            this.selectedProductYear = year;
        },

        setFallbackValue(val) {
            this.fallbackValue = val;
        },

        setAddingItem(bool) {
            this.addingItem = bool;

            if (bool === true) {
                this.itemToAdd.production_year = this.selectedProductYear || new Date().getFullYear() - 1;
            }
        },

        setDeletingItem(item) {
            this.deletingItem = item;
        },

        setMaxProductYear(direction) {
            if (direction === 'left' && this.maxProductYear <= this.years[0] - 1) {
                this.maxProductYear++;
            } else if (direction === 'right' && this.maxProductYear >= this.years[this.years.length -1] + 1) {
                this.maxProductYear--;
            }

            if (this.selectedProductYear < this.productYears[this.productYears.length -1]) {
                this.setSelectedProductYear(this.productYears[this.productYears.length -1]);
            } else if (this.selectedProductYear > this.productYears[0]) {
                this.setSelectedProductYear(this.productYears[0]);
            }
        },

    }
}
</script>

<template>
    <div>
        <div v-if="productYears !== false" class="flex-all-centered-row year-tab-buttons">
            <button class="sust-btn sust-btn-blue"
                @click="setMaxProductYear('left')" :disabled="maxProductYear + 1 > years[0]"
            >
                &larr;
            </button>
            <button v-for="year in productYears" :key="year"
                class="sust-btn product-year-button"
                :class="{
                    '--selected': selectedProductYear === year,
                    '--has-product': items.some(x => x.production_year === year),
                }"
                @click="setSelectedProductYear(year)"
            >
                {{ year }}
            </button>
            <button class="sust-btn sust-btn-blue"
                @click="setMaxProductYear('right')" :disabled="maxProductYear - 1 < years[years.length -1]"
            >
                &rarr;
            </button>
        </div>

        <Card class="items" v-if="!loading">
            <p style="font-weight: bold; font-size: .8vw; color: var(--sustainfinity-navy);" v-if="addingItem">
                {{ $t('companyProfile.modal7.infoNote') }}
            </p>
            <div class="item-headers" v-if="filteredItems.length > 0">
                <div v-for="header in headers" :key="header.key" :style="{ flex: header.flex }">
                    {{ header.title }}
                </div>
            </div>
            
            <div class="item" v-for="(item, i) in filteredItems" :key="i">
                <div class="delete-item-button"
                    :style="{flex: flex_options.small}"
                >
                    <button @click="setDeletingItem(item)">
                        <i class="fa-regular fa-trash-can"></i>
                    </button>

                    <delete-warning v-if="deletingItem == item" @onClose="setDeletingItem(null)" @onOk="deleteItem()" />
                </div>

                <input v-if="selectedReportLanguages.some(x => x === 'en')"
                    type="text" :style="{ flex: flex_options.mid }" v-model="item.product_group_name"
                    @change="updateItem($event, 'product_group_name', item.product_group_id)"
                    @focus="setFallbackValue(item.product_group_name)" @blur="setFallbackValue(null)" />
                
                <input v-if="selectedReportLanguages.some(x => x === 'tr')"
                    type="text" :style="{ flex: flex_options.mid }" v-model="item.product_group_name_tr"
                    @change="updateItem($event, 'product_group_name_tr', item.product_group_id)"
                    @focus="setFallbackValue(item.product_group_name_tr)" @blur="setFallbackValue(null)" />

                <input type="number" :style="{ flex: flex_options.mid }" v-model="item.production_percent"
                    @change="updateItem($event, 'production_percent', item.product_group_id)"
                    @focus="setFallbackValue(item.production_percent)" @blur="setFallbackValue(null)" />

                <input type="number" :style="{ flex: flex_options.mid }" v-model="item.production_count"
                    @change="updateItem($event, 'production_count', item.product_group_id)"
                    @focus="setFallbackValue(item.production_count)" @blur="setFallbackValue(null)" />

                <input type="number" :style="{ flex: flex_options.mid }" v-model="item.production_tonnes"
                    @change="updateItem($event, 'production_tonnes', item.product_group_id)"
                    @focus="setFallbackValue(item.production_tonnes)" @blur="setFallbackValue(null)" />

                <input type="number" :style="{ flex: flex_options.mid }" v-model="item.production_revenue_share"
                    @change="updateItem($event, 'production_revenue_share', item.product_group_id)"
                    @focus="setFallbackValue(item.production_revenue_share)" @blur="setFallbackValue(null)" />
            </div>

            <div v-if="!addingItem && (filteredItems === null || filteredItems.length === 0)" class="flex-all-centered-row" style="padding: 2em;">
                {{ $t('noData.text2') }}
            </div>

            <div v-if="addingItem && filteredItems.length > 0" class="flex-all-centered-row" style="border-bottom: thin solid lightgray; margin: 2em 0;">
                <!-- separator -->
            </div>
            <div class="item-headers" v-if="addingItem">
                <div v-for="header in headers" :key="header.key" :style="{ flex: header.flex }">
                    {{ header.title }}
                </div>
                <div :style="{ flex: flex_options.small}">
                    
                </div>
            </div>
            <div class="item" v-if="addingItem">
                <div class="delete-item-button" :style="{flex: flex_options.small}">
                    <button @click="handleResetAddingItem()" @keyup.enter="handleResetAddingItem()">
                        <i class="fa-regular fa-square-minus"></i>
                    </button>
                </div>

                <input v-if="selectedReportLanguages.some(x => x === 'en')"
                    type="text" :style="{ flex: flex_options.mid }" v-model="itemToAdd.product_group_name"
                    @focus="setFallbackValue(itemToAdd.product_group_name)"
                    @blur="setFallbackValue(null)" />

                <input v-if="selectedReportLanguages.some(x => x === 'tr')"
                    type="text" :style="{ flex: flex_options.mid }" v-model="itemToAdd.product_group_name_tr"
                    @focus="setFallbackValue(itemToAdd.product_group_name_tr)"
                    @blur="setFallbackValue(null)" />

                <input type="number" :style="{ flex: flex_options.mid }" v-model="itemToAdd.production_percent"
                    @focus="setFallbackValue(itemToAdd.production_percent)"
                    @blur="setFallbackValue(null)" />

                <input type="number" :style="{ flex: flex_options.mid }" v-model="itemToAdd.production_count"
                    @focus="setFallbackValue(itemToAdd.production_count)"
                    @blur="setFallbackValue(null)" />

                <input type="number" :style="{ flex: flex_options.mid }" v-model="itemToAdd.production_tonnes"
                    @focus="setFallbackValue(itemToAdd.production_tonnes)"
                    @blur="setFallbackValue(null)" />

                <input type="number" :style="{ flex: flex_options.mid }" v-model="itemToAdd.production_revenue_share"
                    @focus="setFallbackValue(itemToAdd.production_revenue_share)"
                    @blur="setFallbackValue(null)" />

                <button class="sust-btn sust-btn-green" 
                    :style="{ flex: flex_options.small }"
                    @click="createItem()"
                >
                    {{ $t('misc.confirm') }}
                </button>
            </div>

            <div class="add-item-ctr" v-else>
                <button @click="setAddingItem(true)">
                    +
                </button>
            </div>
        </Card>

        <Card v-else>
            <loading-spinner class="flex-all-centered-row" style="font-size: 2em;" />
        </Card>
    </div>
</template>

<style lang='scss' scoped>
.items {
    min-height: 100px !important;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
}

.item-headers {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;

    >div {
        text-align: left;
        margin: .5em;
        color: var(--sustainfinity-navy);
        font-weight: bold;
        font-size: 1.1em;
    }
}

.item {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;

    >.delete-item-button {
        margin: .5em;
        flex: 1;
        display: flex;
        align-items: center;

        >.cannot-edit {
            font-size: .8em;
            font-style: italic;
            color: var(--invalid-input);
        }

        >button {
            display: flex;
            align-items: center;

            outline: 0;
            border: 0;

            background-color: transparent !important;

            &:focus {
                outline: thin solid lightgray !important;
            }

            >i {
                color: var(--invalid-input);
            }

            &:hover {
                >i {
                    color: var(--invalid-input--hover);
                }
            }

            &:disabled {
                pointer-events: none !important;

                >i {
                    color: gray;
                }
            }
        }

        position: relative;

    }

    >input,
    select {
        text-align: left;
        margin: .5em;
        outline: 0;
        border: 0;
        border-bottom: thin solid lightgray;
        background-color: whitesmoke;
        min-width: 0 !important;

        transition: border-bottom 500ms;

        &:focus {
            border-bottom: thin solid black;
        }

        &:disabled {
            border-radius: .2em;
            background-color: rgb(221, 221, 221);
            cursor: not-allowed;
        }
    }
}

.add-item-ctr {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 1em 0;

    >button {
        width: 50px;
        height: 30px;
        font-size: 1.2em;
        font-weight: bold;
        color: white;
        background-color: var(--sustainfinity-yellow);
        border-radius: .25em;

        outline: 0;
        border: 0;

        &:hover {
            background-color: var(--sustainfinity-yellow--hover);
        }

        &:focus {
            outline: thin solid gray !important;
        }
    }
}

.year-tab-buttons {
    width: fit-content;
    text-align: center;
    margin: .5em 0;

    flex-wrap: wrap;
    align-items: stretch;

    > button {
        box-shadow: none;
    }

    > .product-year-button {
        flex: 1;
        margin: 0 .3em;
        padding: .15em 1.5em;
        background-color: whitesmoke;
        color: var(--sustainfinity-navy);

        border: 0 !important;
        outline: 0 !important;

        transition: background-color 150ms;
        cursor: pointer;

        &:hover {
            background-color: lightgray;
        }
    }

    > .--selected {
        background-color: var(--sustainfinity-navy) !important;
        color: whitesmoke !important;
        pointer-events: none !important;
        cursor: default !important;
    }

    > .--has-product {
        background-color: var(--sustainfinity-green);
        color: whitesmoke;

        &:hover {
            background-color: var(--sustainfinity-green--hover);
        }
    }
}
</style>
